import { Injectable } from '@angular/core';
import { ApmBase } from '@elastic/apm-rum';
import { Router, NavigationEnd, RoutesRecognized, ActivatedRouteSnapshot } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ElasticApmCustomLabelService } from '../analytics/elasticAPM-custom-labels.service';
import { LocalCacheService } from '../local-cache/local-cache.service';
import { ElasticAPMGuidService } from './elasticAPM-guid.service';
@Injectable({
  providedIn: 'root'
})
export class ElasticApmRouteTrackingService {
  apm: ApmBase;
  breadcrumbData: any;

  constructor(private router: Router,
    private elasticAPMCustomLabelsService: ElasticApmCustomLabelService, 
    private localCacheService: LocalCacheService,
    private apmGuidService: ElasticAPMGuidService) {
  }

  /**
   * Tracks route changes and adds a route label to the current transaction.
   * 
   * This method subscribes to the router's events and listens for `NavigationEnd` events.
   * When a `NavigationEnd` event occurs, it calls `addRouteLabelToTransaction` with the
   * URL after redirects and the current transaction.
   * 
   * @param apmInstance - The current APM transaction that needs to be labeled with the route information.
   */
  trackRouteChanges(apmInstance: ApmBase) {
    this.router.events.subscribe((data) => {
      if (data instanceof RoutesRecognized) {
        let currentRoute: ActivatedRouteSnapshot = data.state.root;
        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }
        // currentRoute now holds the last child ActivatedRouteSnapshot
        this.breadcrumbData = currentRoute.data['breadcrumb'] || '';
      }
    });
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const userObject = JSON.parse(this.localCacheService.fetchData('msalUserInfo'));
      this.addRouteLabelToTransaction(event.urlAfterRedirects, apmInstance);
      this.elasticAPMCustomLabelsService.addCustomLabels(userObject, apmInstance);//page-load default
    });
  }

  /**
   * Adds route labels to the given transaction based on the provided URL.
   *
   * This method extracts the first and second route segments from the URL after the hash (#)
   * and capitalizes each word in these segments. It then adds these segments as labels
   * ('Parent Route' and 'Child Route') to the provided transaction.
   *
   * @param url - The URL from which to extract route segments.
   * @param apmInstance - The transaction to which the route labels will be added. This parameter is optional.
   */
  private addRouteLabelToTransaction(url: string, apmInstance?: ApmBase) {
    // Extract the first route segment after #
    // Remove the query string if it exists
    const cleanUrl = url.includes('?') ? url.split('?')[0] : url;
    const match1 = cleanUrl.split('/')[1];
    // const match2 = url.split('/')[2];
    let parentRoute = match1 ? this.capitalizeEachWord(match1) : null;
    // const childRoute = match2 ? this.capitalizeEachWord(match2) : null;
    // Extract the segments from the URL
    //const segments = cleanUrl.split('/').filter(segment => segment && isNaN(Number(segment)));

    // Join the remaining segments with hyphens
    let childRoute;
    if (this.breadcrumbData && this.breadcrumbData !== null && this.breadcrumbData !== '') {
      childRoute = this.breadcrumbData;
    }

    //   if(parentRoute === 'Quality' && childRoute === '') {  
    //   parentRoute = 'Water Quality';
    //   childRoute = 'Enterprise';
    // }
    
      if (apmInstance) {
        let userSessionId: string;
        if (this.localCacheService.hasKey('elasticAPMUserSessionId','session')) {
          userSessionId = this.localCacheService.fetchData('elasticAPMUserSessionId', 'session');
        } else {
          userSessionId = this.apmGuidService.getSessionGuid();
          this.localCacheService.storeData('elasticAPMUserSessionId', userSessionId, 'session');
        }
        
        apmInstance?.addLabels({
          parentRoute: parentRoute,
          childRoute: childRoute ? childRoute : parentRoute,
          userSessionId: userSessionId
        });

        // Attach WQ siteId label for Water Quality IQ routes (siteView and systemView)
        // to enable Elastic dashboard reporting on site visits per user
        const siteIdMatch = cleanUrl.match(/water-quality-iq\/(?:siteView|systemView)\/(\d+)/);
        if (siteIdMatch) {
          apmInstance?.addLabels({
            wqSiteId: siteIdMatch[1]
          });
        }

        if (url.includes('/logout')) {
          window.sessionStorage.clear();
        }
      }
  }

  /**
   * Capitalizes the first letter of each word in a given string.
   * Words are defined as sequences of characters separated by spaces or hyphens.
   *
   * @param str - The input string to be transformed.
   * @returns The transformed string with each word capitalized.
   */
  capitalizeEachWord(str: string): string {
    return str.replace(/[- ]+/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }
}